import { useCallback } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAccount } from '../../../common/useAccount'
import { isAdmin, isOwner } from '../../../common/userUtils'
import { type Account } from '../../../models/Account'
import { type AuthenticatedUser } from '../../../models/User'

export const CROSS_CLIENT_AI_PHASE_BLOCKED_TITLE = 'AI Deep Analysis access needed'
export const CROSS_CLIENT_AI_PHASE_PERMISSION_BLOCKED_MESSAGE = 'Ask your admin for access.'
export const CROSS_CLIENT_AI_PHASE_ADMIN_BLOCKED_MESSAGE = 'You can manage this under Settings / Account.'
export const CROSS_CLIENT_AI_PHASE_ACCESS_LOADING_MESSAGE = 'Still checking your permissions. Please try again in a moment.'

// Distinct from a boolean so callers don't conflate "couldn't check" (error) with a confirmed denial.
export type CrossClientAiPhaseAccessRetryResult = 'granted' | 'denied' | 'error'

// Role allowlist overrides (not ORs with) the user-id allowlist: IAM clears user-ids once an enterprise role allowlist is set.
const hasAiPhaseAccessForAccount = (account: Account, currentUser: AuthenticatedUser): boolean => {
  if (!account.effectiveConfig.crossClientQueryAiPhaseAllowed) {
    return false
  }
  const allowedRoleIds = account.effectiveConfig.crossClientQueryAiPhaseAllowedRoles ?? []
  if (allowedRoleIds.length > 0) {
    return currentUser.roles.some((role) => allowedRoleIds.includes(role.id))
  }
  const allowedUserIds = account.effectiveConfig.crossClientQueryAiPhaseAllowedUserIds ?? []
  if (allowedUserIds.length === 0) {
    return true
  }
  return allowedUserIds.includes(currentUser.id)
}

export const useCrossClientAiPhaseAccess = () => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const { account, isFetching, isError, refetch } = useAccount()

  // isError alone signals a failed refetch, since RTK Query keeps serving stale data with isFetching back at false.
  const accountLoading = isFetching || isError
  const hasAiPhaseAccess = !accountLoading && !!account && hasAiPhaseAccessForAccount(account, currentUser)
  const hasAdminAccess = isAdmin(currentUser) || isOwner(currentUser)

  // No configured retry/refetchOnFocus, so an errored query stays that way until an unrelated remount evicts the cache.
  const retryAiPhaseAccess = useCallback(async (): Promise<CrossClientAiPhaseAccessRetryResult> => {
    try {
      const refreshed = await refetch().unwrap()
      return hasAiPhaseAccessForAccount(refreshed.data, currentUser) ? 'granted' : 'denied'
    } catch {
      return 'error'
    }
  }, [refetch, currentUser])

  return { hasAiPhaseAccess, hasAdminAccess, accountLoading, accountError: isError, retryAiPhaseAccess }
}
